import {
  CreateTagTypeMutation,
  TagTypeShowQuery,
  UpdateTagTypeMutation,
  type UpdateTagTypeInput,
} from "@/admin/entities/tagTypes";
import type {
  CreateSchemaDefinition,
  EditSchemaDefinition,
} from "@/shared/components/CRUD/types";
import type { TagTypeShowQueryType } from "@/types/graphql/admin";

/**
 * Схемы форм для InnerCRUD типов тегов организаций
 */

// Значения enum TagPreviewType админской схемы
const PREVIEW_TYPE_OPTIONS = [
  { value: "HORIZONTAL", label: "Альбомная 16:9" },
  { value: "VERTICAL", label: "Портрет 2:3" },
];

// Основная схема - используется и для создания, и для редактирования
export const createSchema: CreateSchemaDefinition = {
  main: {
    label: "Создание типа тега",
    schema: {
      title: {
        required: true,
        label: "Название",
        type: "text",
        xs: 12,
        md: 6,
        fullWidth: true,
      },
      featured: {
        type: "switch",
        label: "Featured",
        helperText:
          "Теги с этим типом будут отображаться как отдельные карточки в облаке тегов",
        xs: 12,
      },
      seoDescription: {
        type: "checkbox",
        label: "В SEO-описание страниц",
        helperText:
          "Названия тегов этого типа добавляются в meta description страниц видео, стримов, коллекций и тегов",
        xs: 12,
      },
      defaultPreviewType: {
        type: "select",
        label: "Основная картинка тегов",
        helperText:
          "Какой слот картинки открывается первым в форме тега: 16:9 для рубрик, портрет 2:3 для людей",
        options: PREVIEW_TYPE_OPTIONS,
        xs: 12,
        md: 6,
        fullWidth: true,
      },
      customFields: {
        type: "customFieldsSchema",
        label: "Поля тегов этого типа",
        addText: "Добавить поле",
        helperText:
          "Поля показываются в форме тега и на странице тега на витрине",
        xs: 12,
      },
    },
    createMutation: CreateTagTypeMutation,
  },
};

// Схема редактирования - идентична схеме создания
export const editSchema: EditSchemaDefinition = {
  main: {
    label: "Редактирование типа тега",
    type: "form" as const,
    schema: {
      title: {
        required: true,
        label: "Название",
        type: "text",
        xs: 12,
        md: 6,
        fullWidth: true,
      },
      featured: {
        type: "switch",
        label: "Featured",
        helperText:
          "Теги с этим типом будут отображаться как отдельные карточки в облаке тегов",
        xs: 12,
      },
      seoDescription: {
        type: "checkbox",
        label: "В SEO-описание страниц",
        helperText:
          "Названия тегов этого типа добавляются в meta description страниц видео, стримов, коллекций и тегов",
        xs: 12,
      },
      defaultPreviewType: {
        type: "select",
        label: "Основная картинка тегов",
        helperText:
          "Какой слот картинки открывается первым в форме тега: 16:9 для рубрик, портрет 2:3 для людей",
        options: PREVIEW_TYPE_OPTIONS,
        xs: 12,
        md: 6,
        fullWidth: true,
      },
      customFields: {
        type: "customFieldsSchema",
        label: "Поля тегов этого типа",
        addText: "Добавить поле",
        helperText:
          "Поля показываются в форме тега и на странице тега на витрине",
        xs: 12,
      },
    },
    editQuery: TagTypeShowQuery,
    updateMutation: UpdateTagTypeMutation,
    getEditData: (data: unknown): UpdateTagTypeInput => {
      const { tagType } = data as TagTypeShowQueryType;
      return {
        title: tagType.title,
        featured: tagType.featured,
        seoDescription: tagType.seoDescription,
        defaultPreviewType: tagType.defaultPreviewType,
        customFields: (tagType.customFields || []).map((field) => ({
          type: field.type,
          name: field.name,
          label: field.label,
          required: field.required ?? false,
          multiple: field.multiple ?? false,
          placeholder: field.placeholder ?? "",
          helperText: field.helperText ?? "",
          options: field.options?.map((option) => ({
            value: option?.value || "",
            label: option?.label || "",
          })),
        })),
      };
    },
  },
};
